import { useMemo, useState } from 'react';
import { Clock, Siren } from 'lucide-react';
import type { Atelier, Demande, Personne, Pole, SeanceProgramme } from '../../types';
import { demainISO, formatHoraire, todayISO } from '../annuaire/format';
import { META_ETAT_BADGE, META_ETAT_LABEL, STATUT_META } from '../demande/format';
import { PoleDot } from '../demande/PoleDot';
import { duree, ilYa, libelleEtapeCourante, retardEscalade } from './regles';

import { dePersonne } from '../messages/format';
// La colonne de gauche de la console : déclarer (une seule entrée, par nom), puis les urgences en cours. DECISIONS §29.

const JOURS_COURTS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
const MOIS_COURTS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
function jourCourt(iso: string): string {
  if (iso === todayISO()) return "Aujourd'hui";
  if (iso === demainISO()) return 'Demain';
  const d = new Date(`${iso}T12:00:00`);
  return `${JOURS_COURTS[d.getDay()]} ${d.getDate()} ${MOIS_COURTS[d.getMonth()]}`;
}
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const MAX_SEANCES = 6;

/** La demande qui « prend en charge » une séance : l'urgence d'abord, sinon une demande en cours, sinon la plus récente. */
function demandeDeLaSeance(l: Demande[] | undefined): Demande | undefined {
  if (!l || l.length === 0) return undefined;
  const termine = (d: Demande) => STATUT_META[d.statut] === 'termine' || STATUT_META[d.statut] === 'sans_suite';
  return l.find((d) => d.statut === 'urgence_cascade') ?? l.find((d) => !termine(d)) ?? l[0];
}

export function DeclarerCarte({ seances, ateliers, personnes, poles, demandesDeLaFenetre, onDeclarer, onOuvrirUrgence, onAutreCas }: {
  seances: SeanceProgramme[]; ateliers: Atelier[]; personnes: Personne[]; poles: Pole[]; demandesDeLaFenetre: Demande[];
  onDeclarer: (s: SeanceProgramme) => void; onOuvrirUrgence: (id: number) => void; onAutreCas: () => void;
}) {
  const [q, setQ] = useState('');
  const [fenetre, setFenetre] = useState<'2j' | '7j'>('2j');
  const atelierParId = useMemo(() => new Map(ateliers.map((a) => [a.id, a])), [ateliers]);
  const nomParId = useMemo(() => new Map(personnes.map((p) => [p.id, p.display_name])), [personnes]);
  const parSeance = useMemo(() => {
    const m = new Map<string, Demande[]>();
    demandesDeLaFenetre.forEach((d) => { const k = `${d.atelier_id}_${String(d.date_atelier).slice(0, 10)}`; (m.get(k) ?? m.set(k, []).get(k)!).push(d); });
    return m;
  }, [demandesDeLaFenetre]);

  const requete = norm(q.trim());
  const triees = useMemo(() => [...seances].filter((s) => atelierParId.has(s.atelier_id))
    .sort((a, b) => (a.date === b.date ? (a.heure_debut ?? '').localeCompare(b.heure_debut ?? '') : a.date.localeCompare(b.date))), [seances, atelierParId]);
  const nomAnime = (s: SeanceProgramme) => nomParId.get(s.animateur_personne_id ?? -1) ?? atelierParId.get(s.atelier_id)?.titulaire_nom ?? 'Animateur·rice inconnu·e';
  const affichees = triees.filter((s) => {
    if (requete) return norm(`${s.atelier_nom} ${nomAnime(s)} ${atelierParId.get(s.atelier_id)?.titulaire_nom ?? ''}`).includes(requete);
    return fenetre === '7j' || s.date === todayISO() || s.date === demainISO();
  });
  const vues = affichees.slice(0, MAX_SEANCES);

  return (
    <section className="card" aria-labelledby="u-decl-t">
      <h2 className="u-titre" id="u-decl-t"><Siren className="icon-sm" aria-hidden="true" /> Déclarer une absence urgente</h2>
      <p className="u-aide">Tapez le nom de la personne absente ou de l'atelier : toutes ses séances des 7 prochains jours apparaissent.</p>
      <div className="search-wrapper u-recherche">
        <input className="search-input" type="search" autoComplete="off" placeholder="Ex. : Alexia, Yoga Montreux…" aria-label="Personne absente ou atelier" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {requete ? (
        <p className="u-hint" style={{ marginBlock: 'var(--s8)' }}>Résultats sur 7 jours pour « {q.trim()} »</p>
      ) : (
        <div className="u-fenetre">
          <div className="selectable-group" role="group" aria-label="Période affichée">
            <button type="button" className={`selectable${fenetre === '2j' ? ' active' : ''}`} aria-pressed={fenetre === '2j'} onClick={() => setFenetre('2j')}>Aujourd'hui et demain</button>
            <button type="button" className={`selectable${fenetre === '7j' ? ' active' : ''}`} aria-pressed={fenetre === '7j'} onClick={() => setFenetre('7j')}>7 jours</button>
          </div>
        </div>
      )}
      {vues.length === 0 ? (
        <p className="u-vide">{requete ? `Aucune séance trouvée pour « ${q.trim()} ». Vérifiez l'orthographe, ou déclarez un autre cas ci-dessous.` : "Aucune séance programmée aujourd'hui ou demain."}</p>
      ) : (
        <ul className="u-seances">
          {vues.map((s) => {
            const at = atelierParId.get(s.atelier_id)!;
            const d = demandeDeLaSeance(parSeance.get(`${s.atelier_id}_${s.date}`));
            const libelle = `${s.atelier_nom}, ${jourCourt(s.date)}, ${formatHoraire(s.heure_debut, s.heure_fin)}`;
            const urgence = d?.statut === 'urgence_cascade';
            const meta = d ? STATUT_META[d.statut] : null;
            return (
              <li key={`${s.atelier_id}_${s.date}_${s.heure_debut}`} className="u-seance">
                <div className="u-seance-t"><PoleDot couleur={poles.find((p) => p.id === at.pole_id)?.couleur} />{s.atelier_nom}<span className="u-seance-h">{formatHoraire(s.heure_debut, s.heure_fin).replace('–', ' – ')}</span></div>
                <div className="u-seance-s">
                  <span>{jourCourt(s.date)} · {nomAnime(s)}</span>
                  {urgence ? <span className="badge badge-err"><Siren className="icon-sm icon-inline" aria-hidden="true" />Urgence en cours</span>
                    : meta ? <span className={META_ETAT_BADGE[meta]}>{META_ETAT_LABEL[meta]}</span>
                    : <span className="badge badge-plain">Aucune demande</span>}
                </div>
                <div className="u-seance-a">
                  {urgence ? <button type="button" className="btn btn-sm" aria-label={`Ouvrir l'urgence : ${libelle}`} onClick={() => onOuvrirUrgence(d!.id)}>Ouvrir</button>
                    : d ? <a className="btn btn-sm btn-ghost" href={`#demandes/${d.id}`} target="_blank" rel="noreferrer" aria-label={`Ouvrir la demande (nouvel onglet) : ${libelle}`}>Ouvrir la demande</a>
                    : <button type="button" className={`btn btn-sm${s.date === todayISO() ? ' btn-danger' : ''}`} aria-label={`Déclarer l'absence ${dePersonne(nomAnime(s))} : ${libelle}`} onClick={() => onDeclarer(s)}>Déclarer</button>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {affichees.length > MAX_SEANCES && <p className="u-vide">Et {affichees.length - MAX_SEANCES} autre{affichees.length - MAX_SEANCES > 1 ? 's' : ''} séance{affichees.length - MAX_SEANCES > 1 ? 's' : ''} : tapez un nom pour les trouver.</p>}
      <p style={{ margin: 'var(--s8) 0 0' }}><button type="button" className="u-lien" onClick={onAutreCas}>Une séance qui n'est pas dans la liste ? Déclarer un autre cas</button></p>
    </section>
  );
}

function Tuile({ d, selectionnee, now, seuilMinutes, onSelect }: { d: Demande; selectionnee: boolean; now: number; seuilMinutes: number; onSelect: (id: number) => void }) {
  const fin = d.statut !== 'urgence_cascade';
  const etapes = d.cascade_etapes ?? [];
  const retard = !fin && retardEscalade(etapes, now, seuilMinutes) > 0;
  const minutes = Math.round((now - new Date(d.declaree?.at ?? d.created_at).getTime()) / 60000);
  const pas = etapes.map((e) => (e.statut === 'reussi' ? 'fait' : e.statut === 'echec' || e.statut === 'saute' ? 'echec' : e.statut === 'en_cours' ? 'encours' : ''));
  const dernier = d.derniere_action;
  const jour = String(d.date_atelier).slice(0, 10);
  const heure = d.atelier_heure_debut ? d.atelier_heure_debut.slice(0, 5) : '';
  return (
    <button type="button" className={`u-tuile${d.statut === 'maintenue' ? ' u-tuile--ok' : fin ? ' u-tuile--fin' : ''}`} aria-current={selectionnee} onClick={() => onSelect(d.id)}>
      <span className="u-tuile-t">
        <span><PoleDot couleur={d.pole_couleur} /> {d.atelier_nom}</span>
        {fin ? <span className={`badge ${d.statut === 'maintenue' ? 'badge-ok' : 'badge-plain'}`}>{d.statut === 'maintenue' ? 'Atelier maintenu' : d.statut === 'annulee' ? 'Atelier annulé' : 'Atelier fusionné'}</span>
          : retard ? <span className="badge badge-err"><Clock className="icon-sm icon-inline" aria-hidden="true" />Délai dépassé</span>
          : <span className="badge badge-accent"><Clock className="icon-sm icon-inline" aria-hidden="true" />{ilYa(minutes)}</span>}
      </span>
      <span className="u-tuile-s">{jourCourt(jour)}{heure ? ` · ${heure}` : ''} · {d.absent_nom}</span>
      {!fin && <span className="u-tuile-etape"><span className="u-pas" aria-hidden="true">{pas.map((c, i) => <i key={i} className={c} />)}</span>{libelleEtapeCourante(etapes)}</span>}
      <span className="u-tuile-s">{!fin && dernier ? `${dernier.par_nom.split(' ')[0]} s'en occupe · ${new Date(dernier.at).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}` : fin ? `Terminée · ${duree(Math.max(0, Math.round((now - new Date(d.updated_at).getTime()) / 60000)))}` : 'Personne n’a encore agi'}</span>
    </button>
  );
}

export function UrgencesEnCours({ ouvertes, terminees, selectedId, now, seuilMinutes, chargement, onSelect }: {
  ouvertes: Demande[]; terminees: Demande[]; selectedId: number | null; now: number; seuilMinutes: number; chargement: boolean; onSelect: (id: number) => void;
}) {
  const ordre = (a: Demande, b: Demande) => String(a.date_atelier).localeCompare(String(b.date_atelier)) || (a.atelier_heure_debut ?? '').localeCompare(b.atelier_heure_debut ?? '');
  const enCours = [...ouvertes].sort(ordre);
  return (
    <section className="card" aria-labelledby="u-urg-t">
      <h2 className="u-titre" id="u-urg-t">Urgences en cours <span className={`badge ${enCours.length ? 'badge-err' : 'badge-plain'}`}>{enCours.length}</span></h2>
      {chargement ? <div className="spinner spinner-sm" /> : enCours.length === 0 ? <p className="u-vide">Aucune urgence en cours.</p>
        : <div className="u-tuiles">{enCours.map((d) => <Tuile key={d.id} d={d} selectionnee={selectedId === d.id} now={now} seuilMinutes={seuilMinutes} onSelect={onSelect} />)}</div>}
      {terminees.length > 0 && (
        <details style={{ marginTop: 'var(--s12)' }} open={terminees.some((d) => d.id === selectedId)}>
          <summary className="u-lien" style={{ display: 'inline-block' }}>Terminées aujourd'hui ({terminees.length})</summary>
          <div className="u-tuiles" style={{ marginTop: 'var(--s8)' }}>{terminees.map((d) => <Tuile key={d.id} d={d} selectionnee={selectedId === d.id} now={now} seuilMinutes={seuilMinutes} onSelect={onSelect} />)}</div>
        </details>
      )}
    </section>
  );
}
